import React from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import './eventCards.css';
import shareIcon from '../../assets/icons/share.svg';
import { buildDeepLink, isSharedScheduleMatch, shareDeepLink, extractStartDateFromRange } from '../../utils/deepLink';
import { NODE_API_BASE } from '../../constant';
const newlyWedIcon = 'https://res.cloudinary.com/dtuy184dc/image/upload/v1775279698/Suprajanan_Newly_Wed_Couples_e0p3gt.png';
const birthUpTo7Icon = 'https://res.cloudinary.com/dtuy184dc/image/upload/v1775279698/Nurturing_Kids_Birth_up_to_7_years_xe0ajs.png';
const stressManagementIcon = 'https://res.cloudinary.com/dtuy184dc/image/upload/v1775279698/Stress_Management_mh1fiq.png';
const healthCareIcon = 'https://res.cloudinary.com/dtuy184dc/image/upload/v1775279699/Health_Care_1_qunv8g.png';
const lifeAfterDeathIcon = 'https://res.cloudinary.com/dtuy184dc/image/upload/v1775279699/Life_after_Death_dnb210.png';
const meditationYogaIcon = 'https://res.cloudinary.com/dtuy184dc/image/upload/v1775279699/Meditation_Yoga_ijktju.png';
const prenatalIcon = 'https://res.cloudinary.com/dtuy184dc/image/upload/v1775279701/Garbha_Sanskar_Prenatal_Education_1_vwf2af.png';
const eightToFourteenIcon = 'https://res.cloudinary.com/dtuy184dc/image/upload/v1775279701/Child_Development_8_to_14_years_jprffn.png';
const scienceOfSpiritualityIcon = 'https://res.cloudinary.com/dtuy184dc/image/upload/v1775279698/Science_of_Spirituality_jzwya1.png';
const fifteenToTwentyEightIcon = 'https://res.cloudinary.com/dtuy184dc/image/upload/v1777011140/Youth_Power_15_to_28_years_1_kkb3ar.png';
const EventScheduleCard = ({
  event_id,
  title,
  description,
  eventDescription,
  dateRange,
  location,
  duration,
  price,
  priceLabel,
  secondPrice,
  secondPriceFor,
  language,
  currencyLabel,
  // New: event subtopics to display under title/description
  subtopics,
  // New: category/topic information for background icon
  categoryLabel,
  topicName,
  isFeatured,
  isSelected,
  ctaLabel,
  onBookNow,
  onNotify,
  isAuthenticated,
  topicId,
  onShare,
  // Optional: schedule_id for future URL improvements (using ID instead of date slug)
  schedule_id,
  // Optional: raw start date (ISO format or Date object) for building deep links
  start_date,
  eventTypeId,
  disabled = false,
  className = '',
  ...rest
}) => {

  // Helper to get unified text for topic/category matching
  const getMatchText = () => {
    return `${categoryLabel || ''} ${topicName || ''} ${title || ''}`.toLowerCase();
  };

  const getNumericTopicId = () => {
    if (!topicId) return null;
    if (typeof topicId === 'object') {
      const rawId = topicId.id || topicId.topic_id || topicId.topicId;
      return rawId ? parseInt(rawId, 10) : null;
    }
    const parsedId = parseInt(topicId, 10);
    return Number.isNaN(parsedId) ? null : parsedId;
  };

  const getCategoryColor = () => {
    const numericTopicId = getNumericTopicId();
    const topicColorById = {
      1: 'blue',
      2: 'blue',
      3: 'pink',
      4: 'pink',
      5: 'green',
      6: 'yellow',
      7: 'purple',
      8: 'yellow',
      9: 'purple',
      10: 'yellow'
    };

    const sourceText = getMatchText();
    if (!sourceText.trim()) {
      return topicColorById[numericTopicId] || 'blue';
    }

    if (sourceText.includes('newly wed') || sourceText.includes('newlywed')) {
      return 'blue';
    }

    if (
      sourceText.includes('birth') ||
      sourceText.includes('upto') ||
      sourceText.includes('up to') ||
      (
        (sourceText.includes('8') || sourceText.includes('eight')) &&
        (sourceText.includes('14') || sourceText.includes('fourteen') || sourceText.includes('to 14') || sourceText.includes('child development'))
      )
    ) {
      return 'pink';
    }

    if (
      (sourceText.includes('life') && sourceText.includes('after') && sourceText.includes('death')) ||
      (sourceText.includes('stress') && sourceText.includes('management')) ||
      (sourceText.includes('meditation') && sourceText.includes('yoga'))
    ) {
      return 'yellow';
    }

    if (
      (sourceText.includes('science') && sourceText.includes('spirituality')) ||
      (sourceText.includes('health') && sourceText.includes('care'))
    ) {
      return 'purple';
    }

    if (
      (sourceText.includes('15') || sourceText.includes('fifteen')) &&
      (sourceText.includes('28') || sourceText.includes('twenty-eight') || sourceText.includes('twenty eight'))
    ) {
      return 'green';
    }

    return topicColorById[numericTopicId] || 'blue';
  };

  const isSharedSchedule = React.useMemo(() =>
    isSharedScheduleMatch({ eventId: event_id, dateRange, location, schedule_id }),
    [event_id, dateRange, location, schedule_id]
  );

  // Compute CTA label based on schedule start date: show 'Notify' if event is >270 days away
  const MS_PER_DAY = 24 * 60 * 60 * 1000;
  const startDateForComparison = React.useMemo(() => {
    const dateStr = extractStartDateFromRange(start_date || dateRange);
    if (!dateStr) return null;
    const d = (typeof dateStr === 'string' || dateStr instanceof String) ? new Date(dateStr) : new Date(dateStr);
    if (isNaN(d.getTime())) return null;
    d.setHours(0, 0, 0, 0);
    return d;
  }, [start_date, dateRange]);

  const daysUntilStart = React.useMemo(() => {
    if (!startDateForComparison) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return Math.ceil((startDateForComparison - today) / MS_PER_DAY);
  }, [startDateForComparison]);

  const shouldShowNotify = daysUntilStart !== null && daysUntilStart > 270;

  const computedCtaLabel = shouldShowNotify ? 'Notify Me' : (ctaLabel || 'Book Now');

  // Determine whether the current user is authenticated.
  const isUserAuthenticated = React.useCallback(() => {
    if (typeof isAuthenticated === 'boolean') return isAuthenticated;
    if (typeof window === 'undefined') return false;
    try {
      // Primary check: user_id (main auth key in this app)
      const userId = localStorage.getItem('user_id');
      if (userId && userId !== 'null' && userId !== 'undefined') {
        return true;
      }
      
      // Fallback: check other auth tokens
      const token = localStorage.getItem('token') || localStorage.getItem('auth_token') || localStorage.getItem('accessToken');
      if (token && token !== 'null' && token !== 'undefined') {
        return true;
      }
      
      return false;
    } catch (e) {
      console.error('[Auth Check] Error checking authentication:', e);
      return false;
    }
  }, [isAuthenticated]);

  // Local subscribed state for this schedule (persisted per-schedule)
  const [subscribed, setSubscribed] = React.useState(() => {
    if (typeof window === 'undefined') return false;
    try {
      if (!schedule_id) return false;
      const saved = localStorage.getItem(`notify_subscribed_${schedule_id}`);
      return saved === '1';
    } catch (e) {
      return false;
    }
  });

  const setPersistSubscribed = React.useCallback((val) => {
    if (typeof window === 'undefined' || !schedule_id) return;
    try {
      localStorage.setItem(`notify_subscribed_${schedule_id}`, val ? '1' : '0');
    } catch (e) {
      // ignore
    }
  }, [schedule_id]);

  // Helper to obtain current user id from localStorage if available
  const getCurrentUserId = React.useCallback(() => {
    try {
      // Primary: direct user_id key used across the app
      const direct = localStorage.getItem('user_id');
      if (direct && direct !== 'null' && direct !== 'undefined') return direct;

      const raw = localStorage.getItem('user') || localStorage.getItem('userInfo') || localStorage.getItem('auth_user');
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      return parsed && (parsed.id || parsed.user_id) ? (parsed.id || parsed.user_id) : null;
    } catch (e) {
      return null;
    }
  }, []);

  // Helper to obtain logged-in user's email from localStorage (various possible keys)
  const getLoggedInUserEmail = React.useCallback(() => {
    try {
      const jsonKeys = ['user', 'userInfo', 'auth_user'];
      for (const k of jsonKeys) {
        const raw = localStorage.getItem(k);
        if (!raw) continue;
        try {
          const p = JSON.parse(raw);
          if (p) {
            const email = p.email || p.emailId || p.user_email || p.email_address || p.userEmail;
            if (email) return String(email).trim();
          }
        } catch (e) {
          // not JSON, skip
        }
      }

      const scalarKeys = ['user_email', 'email', 'email_address', 'userEmail'];
      for (const k of scalarKeys) {
        const v = localStorage.getItem(k);
        if (v) return String(v).trim();
      }
    } catch (e) {
      // ignore
    }
    return null;
  }, []);

  // Attempt to enable notifications for this event.
  const enableNotifyForEvent = React.useCallback(async () => {
    // If parent provided an onNotify handler, delegate to it (it may perform API call + analytics)
    if (typeof onNotify === 'function') {
      try {
        const result = await onNotify({ event_id, schedule_id });
        setPersistSubscribed(true);
        toast.success('You will be notified by email when booking is available before the event.');
        return { ok: true, result };
      } catch (err) {
        console.error('onNotify handler failed', err);
        toast.error('Failed to enable notifications');
        return { ok: false };
      }
    }

    if (typeof window === 'undefined') return { ok: false };

    try {
      const token = localStorage.getItem('token') || localStorage.getItem('auth_token') || localStorage.getItem('accessToken');

      // Try to obtain user_id from stored user object if available
      let userId = null;
      try {
        const raw = localStorage.getItem('user') || localStorage.getItem('userInfo') || localStorage.getItem('auth_user');
        if (raw) {
          const parsed = JSON.parse(raw);
          if (parsed && (parsed.id || parsed.user_id)) userId = parsed.id || parsed.user_id;
        }
      } catch (e) {
        // ignore
      }

      // Build request payload; backend accepts user_id for auth users
      const payload = userId ? { schedule_id, event_id, user_id: userId } : { schedule_id, event_id };

      const baseApi = NODE_API_BASE || '';
      const baseNoApi = String(baseApi).replace(/\/api\/?$/, '');
      const endpoints = [`${baseApi}/notification/subscribe`, `${baseNoApi}/notification/subscribe`];
      let res = null;
      for (const ep of endpoints) {
        try {
          res = await fetch(ep, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...(token ? { Authorization: `Bearer ${token}` } : {})
            },
            body: JSON.stringify(payload)
          });
          if (res) break;
        } catch (e) {
          console.error('[notify] fetch error for', ep, e);
          res = null;
        }
      }

      if (!res) {
        toast.error('Failed to enable notifications');
        return { ok: false };
      }

      const dataText = await res.text().catch(() => '');
      let data = {};
      try { data = dataText ? JSON.parse(dataText) : {}; } catch (e) { data = {}; }
      if (res.ok && (data && (data.status === 'true' || data.status === true))) {
        setPersistSubscribed(true);
        toast.success('Notifications enabled');
        return { ok: true, data };
      }

      // If API returns success even with 200 but status false, still show message from API
      if (data && data.message) toast.info(data.message);
      toast.error('Failed to enable notifications');
      return { ok: false, data };
    } catch (err) {
      console.error('subscribe error', err);
      toast.error('Failed to enable notifications');
      return { ok: false };
    }
  }, [onNotify, event_id, schedule_id]);

  // Click handler for the Notify button: prefer logged-in user's email, otherwise fallback
  const handleNotifyClick = React.useCallback(() => {
    (async () => {
      // If we can read an email for the logged-in user from localStorage, use it directly
      const loggedEmail = getLoggedInUserEmail();
      if (loggedEmail) {
        try {
          const normalized = String(loggedEmail).trim().toLowerCase();
          const uid = getCurrentUserId();
          const payload = uid ? { schedule_id, event_id, user_id: uid, email: normalized } : { schedule_id, event_id, email: normalized };
          const baseApi = NODE_API_BASE || '';
          const baseNoApi = String(baseApi).replace(/\/api\/?$/, '');
          const endpoints = [`${baseApi}/notification/subscribe`, `${baseNoApi}/notification/subscribe`];
          let res = null;
          for (const ep of endpoints) {
            try {
              res = await fetch(ep, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
              });
              if (res) break;
            } catch (e) {
              console.error('[notify] fetch error for', ep, e);
              res = null;
            }
          }

          if (!res) {
            console.error('[notify] no response from any endpoint');
            toast.error('Failed to enable notifications');
            return;
          }

          const dataText = await res.text().catch(() => '');
          let body = {};
          try { body = dataText ? JSON.parse(dataText) : {}; } catch (e) { body = {}; }
          if (res.ok && (body && (body.status === 'true' || body.status === true))) {
            setPersistSubscribed(true);
            setSubscribed(true);
            toast.success('You will be notified by email when booking is available before the event.');
            return;
          }

          if (body && body.message) toast.info(body.message);
          toast.error('Failed to enable notifications');
          return;
        } catch (err) {
          console.error('subscribe with logged email failed', err);
          toast.error('Failed to enable notifications');
          return;
        }
      }

      // If no logged-in email found, fallback to previous flow
      if (isUserAuthenticated()) {
        const res = await enableNotifyForEvent();
        if (res && res.ok) setSubscribed(true);
        return;
      }

      if (typeof window === 'undefined') return;

      // Guest flow: redirect to sign-in page and save intent
      try {
        // Save the notification intent to localStorage
        const intent = {
          event_id,
          schedule_id,
          timestamp: new Date().toISOString()
        };
        localStorage.setItem('notifyIntent', JSON.stringify(intent));
        
        // Save current URL to return after sign-in (using the key that Signin.js checks)
        const currentUrl = window.location.pathname + window.location.search;
        localStorage.setItem('redirect_after_login', currentUrl);
        
        // Show info message
        toast.info('Please sign in to enable notifications');
        
        // Redirect to sign-in page
        setTimeout(() => {
          window.location.href = '/signin';
        }, 500);
      } catch (err) {
        console.error('Failed to save notify intent', err);
        toast.error('Failed to redirect to sign-in');
      }
    })();
  }, [enableNotifyForEvent, isUserAuthenticated, event_id, schedule_id, getLoggedInUserEmail, getCurrentUserId]);

  // On mount: if there is a saved notify intent and user is now authenticated, enable notify and clear intent.
  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      const intentStr = localStorage.getItem('notifyIntent');
      if (!intentStr) return;
      const intent = JSON.parse(intentStr);
      if (!intent || intent.event_id == null) return;
      if (String(intent.event_id) !== String(event_id)) return;
      if (!isUserAuthenticated()) return;
      // Enable and clear stored intent
      enableNotifyForEvent().then(() => {
        try { localStorage.removeItem('notifyIntent'); } catch (e) { /* ignore */ }
      });
    } catch (e) {
      // ignore JSON parse errors
    }
  }, [event_id, enableNotifyForEvent, isUserAuthenticated]);

  // If user is authenticated, fetch their subscriptions to set initial subscribed state
  React.useEffect(() => {
    (async () => {
      if (!isUserAuthenticated() || !schedule_id) return;
      const userId = getCurrentUserId();
      if (!userId) return;
      const token = localStorage.getItem('token') || localStorage.getItem('auth_token') || localStorage.getItem('accessToken');
      const baseApi = NODE_API_BASE || '';
      const baseNoApi = String(baseApi).replace(/\/api\/?$/, '');
      // Prefer filtered call to reduce payload
      const query = `?event_id=${encodeURIComponent(event_id)}&schedule_id=${encodeURIComponent(schedule_id)}`;
      const endpoints = [`${baseApi}/notification/subscriptions/${userId}${query}`, `${baseNoApi}/notification/subscriptions/${userId}${query}`];
      let res = null;
      for (const ep of endpoints) {
        try {
          res = await fetch(ep, { method: 'GET', headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}), Accept: 'application/json' } });
          if (res) break;
        } catch (e) {
          res = null;
        }
      }
      if (!res) return;
      const body = await res.json().catch(() => ({}));

      // New API may return an object with `data.subscribed` (see Postman example)
      // or an array of subscription items as before. Handle both shapes.
      if (res.ok && body && body.data) {
        // Case A: data is an object like { subscribed: true, subscription: { ... } }
        if (typeof body.data === 'object' && !Array.isArray(body.data)) {
          const subscribedFlag = body.data.subscribed === true || body.data.subscribed === 'true';
          if (subscribedFlag) {
            setSubscribed(true);
            setPersistSubscribed(true);
          }
        }

        // Case B: older API returned an array of subscriptions
        if (Array.isArray(body.data)) {
          const found = body.data.find(item => {
            const sched = item.scheduleIdFk || item.scheduleId || item.schedule_id || item.schedule_id_fk || item.scheduleIdFK || item.scheduleid;
            const ev = item.eventIdFk || item.eventId || item.event_id || item.event_id_fk || item.eventIdFK || item.eventid;
            return String(sched) === String(schedule_id) && (event_id == null || String(ev) === String(event_id));
          });
          if (found) {
            setSubscribed(true);
            setPersistSubscribed(true);
          }
        }
      }
    })();
  }, [isUserAuthenticated, schedule_id, getCurrentUserId, event_id, setPersistSubscribed]);

  // Share / Deep-link handler with date included in URL
  const handleShare = async (e) => {
    // Prevent triggering parent navigation
    if (e && e.stopPropagation) e.stopPropagation();

    if (!event_id || !title) {
      toast.error('Unable to share: Event information not available');
      return;
    }

    try {
      const origin = (typeof window !== 'undefined' && window.location && window.location.origin) ? window.location.origin : '';
      // Build minimal deep link with only IDs (no title, date, or location)
      const deepLink = buildDeepLink({ origin, eventId: event_id, schedule_id });

      // Event type: Use eventTypeId if provided, else fallback to categoryLabel/topicName
      let eventType = '';
      if (eventTypeId) {
        const eventTypeMap = {
          1: 'Residential Study Course',
          2: 'One Day Workshop',
          3: 'Machine Test',
          4: 'Rational Poojas',
          5: 'Free Events',
        };
        eventType = eventTypeMap[Number(eventTypeId)] || categoryLabel || topicName || 'Event';
      } else {
        eventType = categoryLabel || topicName || 'Event';
      }

      // Short description: up to 100 characters, plain text, with ellipsis if longer
      let desc = '';
      const descSource = eventDescription || description || '';
      if (descSource) {
        // Remove HTML tags if any
        const stripped = String(descSource).replace(/<[^>]*>/g, '');
        const normalized = stripped.replace(/\s+/g, ' ').trim();
        if (normalized.length <= 100) {
          desc = normalized;
        } else {
          // Try to cut at last space before 100 chars
          const lastSpace = normalized.lastIndexOf(' ', 100);
          if (lastSpace > 0) {
            desc = normalized.slice(0, lastSpace).trim() + '...';
          } else {
            desc = normalized.slice(0, 100).trim() + '...';
          }
        }
      }

      // Compose share text in required format (fixed):
      // Book the <<Event type>>: <<Event Name>> – <<Date Range>> at <<Location>>\n<<Short description up to 100 characters...>>\n\n<URL>
      const shareText = `Book the ${eventType}: ${title}${dateRange ? ` – ${dateRange}` : ''}${location ? ` at ${location}` : ''}\n${desc}`;

      // Try native share first
      if (typeof navigator !== 'undefined' && navigator.share) {
        try {
          await navigator.share({
            title: title || 'Event',
            text: shareText,
            url: deepLink
          });
          if (onShare) onShare(deepLink);
          return;
        } catch (err) {
          // If user cancels or share fails, fall through to next fallback
        }
      }
      // Try clipboard
      if (typeof navigator !== 'undefined' && navigator.clipboard && navigator.clipboard.writeText) {
        try {
          await navigator.clipboard.writeText(shareText);
          if (onShare) onShare(deepLink);
          return;
        } catch (err) {
          // If clipboard fails, fall through to prompt
        }
      }
      // Always try prompt as last fallback if available
      if (typeof window !== 'undefined' && typeof window.prompt === 'function') {
        try {
          window.prompt('Copy this link:', shareText);
          if (onShare) onShare(deepLink);
          return;
        } catch (err) {
          // If prompt fails, show error
          toast.error('Failed to open share dialog');
          return;
        }
      }
      // If all else fails
      toast.error('Failed to share link');
    } catch (err) {
      console.error('Share failed', err);
      toast.error('Failed to share link');
    }
  };

  // Get background icon based on topic/category label
  const getBackgroundIcon = () => {

    const topicMapping = {
      1: newlyWedIcon,
      2: prenatalIcon,  
      3: birthUpTo7Icon,
      4: eightToFourteenIcon,
      5: fifteenToTwentyEightIcon,
      6: stressManagementIcon,
      7: healthCareIcon,
      8: meditationYogaIcon,
      9: scienceOfSpiritualityIcon,
      10: lifeAfterDeathIcon
    };

    // Extract topic ID - handle both object {id: 2} and primitive (2 or "2")
    const numericTopicId = getNumericTopicId();
    
    
    
    return numericTopicId ? (topicMapping[numericTopicId] || null) : null; 
    // BROADENED: Allow matching even if categoryLabel/topicName are empty, use title as fallback
    // const label = getMatchText();
    
    // // If label is completely empty, return null
    // if (!label || label.trim() === '') return null;
    
    // // Map topic names to their corresponding icons
    // // Check in order of specificity (more specific patterns first)
    
    // // Garbha Samarthya / Prenatal / Suprajanan
    // if (label.includes('garbha') || label.includes('samarthya') || label.includes('prenatal') || label.includes('suprajanan')) {
    //   return prenatalIcon;
    // }
    // // Birth up to 7 years - check for topic name and common event names in this category
    // else if (
    //   label.includes('birth') && (label.includes('7') || label.includes('upto') || label.includes('up to') || label.includes('to 7')) ||
    //   label.includes('mendu') ||  // Mendu-Kranti (Brain Revolution)
    //   label.includes('kranti') ||
    //   label.includes('nurturing') // Nurturing Kids
    // ) {
    //   return birthUpTo7Icon;
    // }
    // // 15 to 28 years (check before 8 to 14 to avoid conflicts)
    // else if ((label.includes('15') || label.includes('fifteen')) && (label.includes('28') || label.includes('twenty-eight') || label.includes('twenty eight'))) {
    //   return fifteenToTwentyEightIcon;
    // }
    // // 8 to 14 years
    // else if ((label.includes('8') || label.includes('eight')) && (label.includes('14') || label.includes('fourteen') || label.includes('to 14'))) {
    //   return eightToFourteenIcon;
    // }
    // // Newly Wed Couples
    // else if (label.includes('newly wed') || label.includes('newlywed') || label.includes('suprajanan')) {
    //   return newlyWedIcon;
    // }
    // // Stress Management
    // else if (label.includes('stress') && label.includes('management')) {
    //   return stressManagementIcon;
    // }
    // // Meditation & Yoga
    // else if (label.includes('meditation') && label.includes('yoga')) {
    //   return meditationYogaIcon;
    // }
    // // Life After Death
    // else if (label.includes('life') && label.includes('after') && label.includes('death')) {
    //   return lifeAfterDeathIcon;
    // }
    // // Health Care
    // else if (label.includes('health') && label.includes('care')) {
    //   return healthCareIcon;
    // }
    // // Prenatal
    // else if (label.includes('prenatal')) {
    //   return prenatalIcon;
    // }
    // // Science of Spirituality
    // else if (label.includes('science') && label.includes('spirituality')) {
    //   return scienceOfSpiritualityIcon;
    // }
    
    // return null;
  };

  // Check topic type for specific styling classes
  const isBirthUpTo7Topic = () => {
    const text = getMatchText();
    return text.includes('birth') && (text.includes('7') || text.includes('upto') || text.includes('up to') || text.includes('to 7'));
  };

  const isNewlyWedTopic = () => {
    const text = getMatchText();
    return text.includes('newly wed') || text.includes('newlywed') || text.includes('suprajanan');
  };

  const isMeditationYogaTopic = () => {
    const text = getMatchText();
    return text.includes('meditation') && text.includes('yoga');
  };

  const isStressManagementTopic = () => {
    const text = getMatchText();
    return text.includes('stress') && text.includes('management');
  };

  const isLifeAfterDeathTopic = () => {
    const text = getMatchText();
    return text.includes('life') && text.includes('after') && text.includes('death');
  };

  const isScienceOfSpiritualityTopic = () => {
    const text = getMatchText();
    return text.includes('science') && text.includes('spirituality');
  };

  const isHealthCareTopic = () => {
    const text = getMatchText();
    return text.includes('health') && text.includes('care');
  };

  const isFifteenToTwentyEightTopic = () => {
    const text = getMatchText();
    return (
      (text.includes('15') || text.includes('fifteen')) &&
      (text.includes('28') || text.includes('twenty-eight') || text.includes('twenty eight'))
    );
  };

  const needsLeftAdjustedPosition = () => {
    const label = getMatchText();
    return (
      ((label.includes('8') || label.includes('eight')) && (label.includes('14') || label.includes('fourteen') || label.includes('to 14')))
    );
  };

  // Get the background icon to display
  const displayBackgroundIcon = getBackgroundIcon();
  
  
  
  // Determine background styling class
  let backgroundClass = '';
  if (displayBackgroundIcon) {
    const numericTopicId = getNumericTopicId();
    if (isBirthUpTo7Topic() || numericTopicId === 3 || numericTopicId === 4) {
      backgroundClass = 'event-schedule-card__background--birth-up-to-7';
    } else if (isMeditationYogaTopic() || numericTopicId === 8) {
      backgroundClass = 'event-schedule-card__background--meditation-yoga';
    } else if (isStressManagementTopic() || isLifeAfterDeathTopic() || numericTopicId === 6 || numericTopicId === 10) {
      backgroundClass = 'event-schedule-card__background--life-after-death';
    } else if (needsLeftAdjustedPosition()) {
      backgroundClass = 'event-schedule-card__background--left-adjusted';
    } else if (isNewlyWedTopic() || numericTopicId === 1) {
      backgroundClass = 'event-schedule-card__background--newly-wed';
    } else if (isScienceOfSpiritualityTopic() || numericTopicId === 9) {
      backgroundClass = 'event-schedule-card__background--science-of-spirituality';
    } else if (isHealthCareTopic() || numericTopicId === 7) {
      backgroundClass = 'event-schedule-card__background--health-care';
    } else if (isFifteenToTwentyEightTopic() || numericTopicId === 5) {
      backgroundClass = 'event-schedule-card__background--fifteen-to-twenty-eight';
    } else {
      backgroundClass = 'event-schedule-card__background--topic-icon';
    }
    
  }

  // If dateRange is a start - end string and both sides are identical,
  // collapse it to a single date string for display (avoid "X - X").
  const getDisplayDateRange = () => {
    if (!dateRange) return '';
    const parts = String(dateRange).split(/\s*-\s*/);
    if (parts.length === 2 && parts[0].trim() === parts[1].trim()) return parts[0].trim();
    return dateRange;
  };
  const displayDate = getDisplayDateRange();

  // --- Location Popup/Modal State and Handlers ---
  const [locationPopup, setLocationPopup] = React.useState({ show: false, address: '', googleMapsUrl: '', position: { top: 0, left: 0, arrowLeft: 15 } });
  // Track if scroll listener is attached
  const scrollListenerRef = React.useRef(false);
  const [mobileModal, setMobileModal] = React.useState({ show: false, address: '', googleMapsUrl: '' });
  const locationPopupTimer = React.useRef(null);

  // State for address and Google Maps link
  const [addressState, setAddressState] = React.useState({
    fullAddress: rest.fullAddress || rest.venueAddress || location,
    googleMapsUrl: rest.googleMapsUrl || rest.url || ''
  });

  // Fetch schedule data from calendar API if missing
  React.useEffect(() => {
    if (addressState.googleMapsUrl && addressState.fullAddress) return;
    // Only fetch if we have event_id and schedule_id
    if (!event_id || !schedule_id) return;
    // Use same API as EventsCalendarPage
    const lang = (localStorage.getItem('key') === 'ma') ? 'ma' : 'en';
    // topic_id, city_id, event_type_id, start_date: use 0/all for broadest match
    const apiUrl = `${NODE_API_BASE}/calendar/list/${lang}/0/0/0/all`;
    fetch(apiUrl)
      .then(res => res.json())
      .then(data => {
        if (data && data.status === 'true' && Array.isArray(data.data)) {
          // Flatten all schedules
          const allSchedules = data.data.flatMap(group => group.arrSchedule || []);
          // Find matching schedule
          const match = allSchedules.find(sch => {
            // Try to match by scheduleId and eventId
            const sid = sch.scheduleId || sch.schedule_id;
            const eid = sch.eventId || sch.event_id;
            return String(sid) === String(schedule_id) && String(eid) === String(event_id);
          });
          if (match) {
            setAddressState({
              fullAddress: match.venueAddress || '',
              googleMapsUrl: match.googleMapsUrl || match.url || ''
            });
          }
        }
      })
      .catch(() => {});
  }, [event_id, schedule_id, addressState.googleMapsUrl, addressState.fullAddress]);

  const fullAddress = addressState.fullAddress;
  const googleMapsUrl = addressState.googleMapsUrl;

  const handleLocationHover = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (locationPopupTimer.current) {
      clearTimeout(locationPopupTimer.current);
      locationPopupTimer.current = null;
    }
    // Position popup below the icon
    const rect = e.currentTarget.getBoundingClientRect();
    const popupWidth = 220;
    const popupHeight = 120;
    const iconCenterX = rect.left + (rect.width / 2);
    let top = rect.bottom + 5;
    let left = iconCenterX - (popupWidth / 2);
    let arrowPosition = popupWidth / 2;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    if (left + popupWidth > viewportWidth) {
      left = viewportWidth - popupWidth - 10;
      arrowPosition = iconCenterX - left;
      if (arrowPosition < 10) arrowPosition = 10;
      if (arrowPosition > popupWidth - 10) arrowPosition = popupWidth - 10;
    }
    if (left < 10) {
      left = 10;
      arrowPosition = iconCenterX - left;
      if (arrowPosition < 10) arrowPosition = 10;
      if (arrowPosition > popupWidth - 10) arrowPosition = popupWidth - 10;
    }
    if (top + popupHeight > viewportHeight) {
      top = rect.top - popupHeight - 5;
    }
    setLocationPopup({
      show: true,
      address: fullAddress,
      googleMapsUrl: googleMapsUrl,
      position: { top, left, arrowLeft: arrowPosition }
    });
    // Add scroll listener to close popup on scroll
    if (!scrollListenerRef.current) {
      window.addEventListener('scroll', closeLocationPopupOnScroll, true);
      scrollListenerRef.current = true;
    }
  };

  // Close popup on scroll
  const closeLocationPopupOnScroll = React.useCallback(() => {
    setLocationPopup(p => ({ ...p, show: false }));
    if (scrollListenerRef.current) {
      window.removeEventListener('scroll', closeLocationPopupOnScroll, true);
      scrollListenerRef.current = false;
    }
  }, []);

  const handleLocationLeave = () => {
    if (!locationPopupTimer.current) {
      locationPopupTimer.current = setTimeout(() => {
        setLocationPopup(p => ({ ...p, show: false }));
        locationPopupTimer.current = null;
        // Remove scroll listener when popup closes
        if (scrollListenerRef.current) {
          window.removeEventListener('scroll', closeLocationPopupOnScroll, true);
          scrollListenerRef.current = false;
        }
      }, 150);
    }
  };

  const handleLocationPopupEnter = () => {
    if (locationPopupTimer.current) {
      clearTimeout(locationPopupTimer.current);
      locationPopupTimer.current = null;
    }
  };

  const handleLocationPopupLeave = () => {
    if (!locationPopupTimer.current) {
      locationPopupTimer.current = setTimeout(() => {
        setLocationPopup(p => ({ ...p, show: false }));
        locationPopupTimer.current = null;
        // Remove scroll listener when popup closes
        if (scrollListenerRef.current) {
          window.removeEventListener('scroll', closeLocationPopupOnScroll, true);
          scrollListenerRef.current = false;
        }
      }, 150);
    }
  };
  // Clean up scroll listener on unmount
  React.useEffect(() => {
    return () => {
      if (scrollListenerRef.current) {
        window.removeEventListener('scroll', closeLocationPopupOnScroll, true);
        scrollListenerRef.current = false;
      }
    };
  }, [closeLocationPopupOnScroll]);

  const handleLocationClickMobile = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setMobileModal({ show: true, address: fullAddress, googleMapsUrl });
  };

  const closeMobileModal = () => setMobileModal({ show: false, address: '', googleMapsUrl: '' });

  return (
    <article 
      className={`event-card event-schedule-card event-schedule-card--${getCategoryColor()} ${(isSharedSchedule || isSelected) ? 'event-schedule-card--highlighted' : ''} ${className}`.trim()} 
      {...rest}
    >
      {/* Share button (deep-link / copy) - top-right */}
      <button
        type="button"
        className="event-schedule-card__share-btn"
        onClick={(e) => handleShare(e)}
        aria-label="Share event"
        title="Share"
        style={{ position: 'absolute', top: '12px', right: '12px', left: 'auto', display: 'inline-flex', width: 'auto', background: 'transparent', border: 'none', cursor: 'pointer', padding: '8px', zIndex: 2 }}
      >
        <img src={shareIcon} alt="Share" className="event-schedule-card__share-icon" style={{ width: '20px', height: '20px' }} />
      </button>
      
      {/* Background decorative illustration */}
      {displayBackgroundIcon && (
        <div className={`event-schedule-card__background ${backgroundClass}`}>
          <img 
            src={displayBackgroundIcon} 
            alt="" 
            aria-hidden="true"
          />
        </div>
      )}
      
      {(isSelected || isSharedSchedule) && <span className="event-schedule-card__badge event-schedule-card__badge--selected">Selected</span>}
      {isFeatured && !isSelected && !isSharedSchedule && <span className="event-schedule-card__badge">Featured</span>}

      <p className="event-schedule-card__date">{displayDate}</p>
      <span className="event-schedule-card__location" style={{display:'flex',alignItems:'flex-start',gap:6,position:'relative'}}>
        <span>{location}</span>
        <i
          className="fas fa-map-marker-alt"
          style={{ color: '#dc3545', cursor: 'pointer', marginLeft: 4, marginTop: 2 }}
          onMouseEnter={(e) => {
            if (window.innerWidth >= 768) handleLocationHover(e);
          }}
          onMouseLeave={(e) => {
            if (window.innerWidth >= 768) handleLocationLeave(e);
          }}
          onClick={(e) => {
            if (window.innerWidth < 768) handleLocationClickMobile(e);
          }}
          tabIndex={0}
          aria-label="Show full address"
        />
      </span>
      {/* Location Popup (desktop) */}
      {locationPopup.show && (
        <div
          className="location-popup"
          style={{
            position: 'fixed',
            top: `${locationPopup.position.top}px`,
            left: `${locationPopup.position.left}px`,
            zIndex: 9999,
            minWidth: 220,
            background: '#fff',
            border: '1px solid #9d0b0e',
            borderRadius: 8,
            boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
            padding: 12,
            fontSize: '0.95rem',
            '--arrow-left': `${locationPopup.position.arrowLeft || 110}px`
          }}
          onMouseEnter={handleLocationPopupEnter}
          onMouseLeave={handleLocationPopupLeave}
        >
          <div style={{ marginBottom: 6 }}><strong>Address:</strong></div>
          <div style={{ marginBottom: 8, wordWrap: 'break-word' }}>{locationPopup.address || 'Address not available'}</div>
          {locationPopup.googleMapsUrl ? (
            <div>
              <a href={locationPopup.googleMapsUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#007bff', textDecoration: 'underline', wordBreak: 'break-all', fontSize: '0.85rem' }}>
                {locationPopup.googleMapsUrl}
              </a>
            </div>
          ) : (
            <div style={{ fontSize: '0.85rem', color: '#6c757d', fontStyle: 'italic' }}>Google Maps link not available</div>
          )}
        </div>
      )}

      {/* Mobile Modal for location (mobile only) */}
      {mobileModal.show && (
        <div
          className="mobile-modal-overlay"
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            zIndex: 10000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
          onClick={closeMobileModal}
        >
          <div
            className="mobile-modal-box"
            style={{
              background: '#fff',
              padding: 16,
              borderRadius: 8,
              maxWidth: '92%',
              width: 340,
              boxShadow: '0 8px 24px rgba(0,0,0,0.2)'
            }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <strong>Address</strong>
              <button className="btn btn-sm btn-light mob-close-btn" onClick={closeMobileModal}>Close</button>
            </div>
            <div style={{ marginTop: 8, fontSize: '0.9rem' }}>
              <div style={{ marginBottom: 8, wordBreak: 'break-word' }}>{mobileModal.address || 'Address not available'}</div>
              {mobileModal.googleMapsUrl ? (
                <a href={mobileModal.googleMapsUrl} target="_blank" rel="noopener noreferrer">Open in Google Maps</a>
              ) : (
                <div style={{ color: '#6c757d', fontStyle: 'italic' }}>Google Maps link not available</div>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="event-schedule-card__meta">
        <span className="event-schedule-card__meta-item">
          <i className="far fa-clock" aria-hidden="true"></i>
          {duration}
        </span>
        <span className="event-schedule-card__meta-divider">|</span>
        <span className="event-schedule-card__meta-item">
          <i className="fas fa-rupee-sign" aria-hidden="true"></i>
          {currencyLabel} {price}/- {priceLabel}
        </span>
         {/* Second Price */}
          {secondPrice && (
              <span className="event-schedule-card__meta-item">
                <i className="fas fa-rupee-sign" aria-hidden="true"></i>
                 {currencyLabel} {secondPrice}/- {secondPriceFor}
              </span>
          )}
      </div>
      
      <p className="event-schedule-card__language">{language}</p>
      

      {shouldShowNotify ? (
        subscribed ? (
          <button
            type="button"
            className="event-schedule-card__cta event-schedule-card__cta--notify event-schedule-card__cta--enabled"
            disabled
            aria-label="Notifications enabled"
            title="Notifications enabled"
          >
            <span className="event-schedule-card__notify-text">Notification Enabled</span>
          </button>
        ) : (
          <button
            type="button"
            className="event-schedule-card__cta event-schedule-card__cta--notify"
            onClick={handleNotifyClick}
            aria-label="Notify me about this event"
          >
            {/* Use PNG as mask so it inherits color (bell.png should be placed at src/assets/icons/bell.png) */}
            <span className="event-schedule-card__notify-icon event-schedule-card__notify-icon--mask" aria-hidden="true" />
            <span className="event-schedule-card__notify-text">{computedCtaLabel}</span>
          </button>
        )
      ) : (
          <button
            type="button"
            className={`event-schedule-card__cta ${disabled ? 'event-schedule-card__cta--disabled' : ''}`}
            onClick={() => {
              if (disabled) return;
              onBookNow && onBookNow();
            }}
            disabled={disabled}
            aria-disabled={disabled}
          >
            {computedCtaLabel}
          </button>
      )}
    </article>
  );
};

EventScheduleCard.propTypes = {
  event_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  eventDescription: PropTypes.string,
  dateRange: PropTypes.string.isRequired,
  location: PropTypes.string.isRequired,
  duration: PropTypes.string.isRequired,
  price: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  priceLabel: PropTypes.string,
  language: PropTypes.string,
  currencyLabel: PropTypes.string,
  subtopics: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.object])),
  categoryLabel: PropTypes.string,
  topicName: PropTypes.string,
  topicId: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.object]),
  isFeatured: PropTypes.bool,
  isSelected: PropTypes.bool,
  ctaLabel: PropTypes.string,
  onBookNow: PropTypes.func,
  onNotify: PropTypes.func,
  isAuthenticated: PropTypes.bool,
  schedule_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  start_date: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
  className: PropTypes.string,
  eventTypeId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  disabled: PropTypes.bool,
};

EventScheduleCard.defaultProps = {
  description: '',
  eventDescription: '',
  priceLabel: 'per Person',
  language: 'English',
  currencyLabel: 'INR',
  subtopics: [],
  categoryLabel: '',
  topicName: '',
  topicId: null,
  isFeatured: false,
  isSelected: false,
  ctaLabel: 'Book Now',
  onBookNow: () => {},
  onNotify: null,
  isAuthenticated: false,
  schedule_id: null,
  start_date: null,
  className: '',
  eventTypeId: null,
  disabled: false,
};

export default EventScheduleCard;

